import { MNSproCloudApi } from '..';
import {
  Appointment,
  CreateAppointmentRequestBody,
  CreateEventBody,
  News,
  Recipient,
  UpdateAppointmentRequestBody,
} from '../api/v2';

export interface NewsUpdatePayload {
  title: string;
  description: string;
  recipients?: Array<string> | null;
  publishDate?: string | null;
}

export class ParentalCommunicationService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ParentalCommunicationApiFactory(config);
    return api;
  }

  static async getAllNews(
    authToken: string,
    organizationId: string,
  ): Promise<News[]> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.getAllNews(organizationId);
    return response.data;
  }

  /**
   * Gets the newest news of the calling user, scoped by role: teachers get the news they
   * published or received, everyone else the news of their own classes and grades. Backs the
   * cockpit news widget and returns the same payload as the Teams cockpit endpoint.
   *
   * @param authToken - Active MNSpro Cloud access token.
   * @param organizationId - Id of the organization the news belong to.
   * @returns The newest news items of the calling user.
   */
  static async getMyNews(
    authToken: string,
    organizationId: string,
  ): Promise<News[]> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.getMyNews(organizationId);
    return response.data;
  }

  static async getAllRecipients(
    authToken: string,
    organizationId: string,
  ): Promise<Recipient[]> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.getAllRecipients(organizationId);
    return response.data;
  }

  static async createNews(
    authToken: string,
    organizationId: string,
    title: string,
    description: string,
    recipients: Recipient[] | null,
    pushNotification: boolean,
    publishDate: string | null,
  ): Promise<News> {
    const api = ParentalCommunicationService.getApi(authToken);
    const createEventBody: CreateEventBody = {
      title,
      description,
      pushNotification,
      recipients: recipients === null ? null : recipients.map(r => r.id ?? ''),
      publishDate,
    };
    const response = await api.createNews(organizationId, createEventBody);
    return response.data;
  }

  static async updateNews(
    authToken: string,
    organizationId: string,
    newsId: string,
    updates: NewsUpdatePayload,
  ): Promise<News> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.updateNews(organizationId, newsId, updates);

    return response.data;
  }

  static async deleteNews(
    authToken: string,
    organizationId: string,
    newsId: string,
  ) {
    const api = ParentalCommunicationService.getApi(authToken);
    await api.deleteNews(organizationId, newsId);
  }

  static async getAllAppointments(
    authToken: string,
    organizationId: string,
  ): Promise<Appointment[]> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.getAllAppointments(organizationId);

    return response.data;
  }

  static async createAppointment(
    authToken: string,
    organizationId: string,
    createRequest: CreateAppointmentRequestBody,
  ): Promise<Appointment> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.createAppointment(organizationId, createRequest);

    return response.data;
  }

  static async updateAppointment(
    authToken: string,
    organizationId: string,
    request: UpdateAppointmentRequestBody,
  ): Promise<Appointment> {
    const api = ParentalCommunicationService.getApi(authToken);
    const response = await api.updateAppointment(organizationId, request);

    return response.data;
  }

  static async deleteAppointment(
    authToken: string,
    organizationId: string,
    appointmentId: string,
  ) {
    const api = ParentalCommunicationService.getApi(authToken);
    await api.deleteAppointment(organizationId, appointmentId);
  }
}
